import { agencyInfo } from '../../../mocks/dashboard';

const PLAN_FEATURES = {
  Starter: ['Up to 2 staff', 'Up to 100 bookings/mo', 'Basic CRM', 'Invoice generation', 'Email support'],
  Pro:     ['Up to 10 staff', 'Unlimited bookings', 'Full CRM + Pipeline', 'Invoices & Vouchers', 'Itinerary Builder', 'Products Catalog', 'Priority support', 'WhatsApp integration'],
  Elite:   ['Unlimited staff', 'Unlimited bookings', 'All Pro features', 'Custom domain', 'White-label PDFs', 'API access', 'Dedicated account manager', '24/7 phone support'],
};

interface UsageStat { label: string; used: number; total: number; unit: string; icon: string }

const usageStats: UsageStat[] = [
  { label: 'Staff Members',   used: 7,    total: 10,    unit: 'seats',    icon: 'ri-team-line' },
  { label: 'Bookings (MTD)',  used: 347,  total: 999,   unit: 'bookings', icon: 'ri-calendar-check-line' },
  { label: 'Invoices (MTD)',  used: 12,   total: 999,   unit: 'invoices', icon: 'ri-bill-line' },
  { label: 'Storage',        used: 2.4,  total: 10,    unit: 'GB',       icon: 'ri-hard-drive-2-line' },
];

interface Invoice { date: string; amount: string; status: string; ref: string }
const invoices: Invoice[] = [
  { date: 'Mar 01, 2026', amount: 'AED 899', status: 'Paid', ref: 'INV-SW-2026-03' },
  { date: 'Feb 01, 2026', amount: 'AED 899', status: 'Paid', ref: 'INV-SW-2026-02' },
  { date: 'Jan 01, 2026', amount: 'AED 899', status: 'Paid', ref: 'INV-SW-2026-01' },
  { date: 'Dec 01, 2025', amount: 'AED 899', status: 'Paid', ref: 'INV-SW-2025-12' },
];

export default function BillingSection() {
  const brand = agencyInfo.brandColor;
  const currentPlan = agencyInfo.plan as 'Starter' | 'Pro' | 'Elite';

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}cc)` }}>
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 -translate-y-10 translate-x-10" style={{ backgroundColor: 'white' }} />
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">Current Plan</p>
            <h3 className="text-3xl font-bold">Pro</h3>
            <p className="text-white/80 text-sm mt-1">AED 899 / month · Billed monthly</p>
            <p className="text-white/60 text-xs mt-1">Next renewal: Apr 01, 2026</p>
          </div>
          <div className="text-right">
            <span className="inline-block bg-white/20 text-white text-xs px-3 py-1.5 rounded-full font-medium">Active</span>
            <p className="text-white/70 text-xs mt-2">Member since Jan 2024</p>
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <button className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-white text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">
            Manage Plan
          </button>
          <button className="px-4 py-2 bg-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors" style={{ color: brand }}>
            Upgrade to Elite
          </button>
        </div>
      </div>

      {/* Usage Stats */}
      <div>
        <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-4">Usage This Month</p>
        <div className="grid grid-cols-2 gap-4">
          {usageStats.map((stat) => {
            const pct = Math.min(100, (stat.used / stat.total) * 100);
            const barColor = pct >= 90 ? '#EF4444' : pct >= 70 ? '#F59E0B' : brand;
            return (
              <div key={stat.label} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${brand}12` }}>
                    <i className={`${stat.icon} text-sm`} style={{ color: brand }} />
                  </div>
                  <p className="text-sm font-medium text-gray-700">{stat.label}</p>
                </div>
                <div className="flex items-end justify-between mb-1.5">
                  <span className="text-xl font-bold text-gray-900">{stat.used}</span>
                  <span className="text-xs text-gray-400">of {stat.total} {stat.unit}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Plan Comparison */}
      <div>
        <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-4">Plan Comparison</p>
        <div className="grid grid-cols-3 gap-3">
          {(['Starter', 'Pro', 'Elite'] as const).map((plan) => {
            const isCurrent = plan === currentPlan;
            return (
              <div key={plan} className={`border rounded-2xl p-4 ${isCurrent ? 'border-2' : 'border-gray-100'}`}
                style={isCurrent ? { borderColor: brand } : {}}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-bold text-gray-900">{plan}</p>
                  {isCurrent && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: brand }}>Current</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mb-3">
                  {plan === 'Starter' ? 'AED 299/mo' : plan === 'Pro' ? 'AED 899/mo' : 'AED 1,999/mo'}
                </p>
                <div className="space-y-1.5">
                  {PLAN_FEATURES[plan].map((feat) => (
                    <div key={feat} className="flex items-start gap-1.5 text-xs text-gray-600">
                      <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
                {!isCurrent && (
                  <button className="w-full mt-4 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 cursor-pointer">
                    {plan === 'Starter' ? 'Downgrade' : 'Upgrade'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment Method */}
      <div className="border border-gray-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-400 uppercase font-medium tracking-wider">Payment Method</p>
          <button className="text-xs font-medium cursor-pointer" style={{ color: brand }}>Change</button>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-9 bg-gray-900 rounded-lg flex items-center justify-center">
            <i className="ri-visa-line text-white text-xl" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">Visa •••• 4242</p>
            <p className="text-xs text-gray-400">Expires 08/2028 · Billing: Dubai, UAE</p>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div>
        <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Billing History</p>
        <div className="border border-gray-100 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-400">
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {invoices.map((inv) => (
                <tr key={inv.ref} className="hover:bg-gray-50/50">
                  <td className="px-5 py-3 text-gray-600">{inv.date}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{inv.ref}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-800">{inv.amount}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">{inv.status}</span>
                      <button className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-pointer">
                        <i className="ri-download-line text-xs" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
